import {
  WebGLRenderer,
  Scene,
  Camera,
  Vector2,
} from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { VISUAL } from '../constants';

// Vignette shader
const VignetteShader = {
  uniforms: {
    tDiffuse: { value: null },
    offset: { value: 1.0 },
    darkness: { value: 1.2 },
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float offset;
    uniform float darkness;
    varying vec2 vUv;
    void main() {
      vec4 texel = texture2D(tDiffuse, vUv);
      vec2 uv = (vUv - 0.5) * 2.0;
      float vignette = 1.0 - dot(uv, uv) * darkness * 0.25;
      vignette = clamp(vignette, 0.0, 1.0);
      texel.rgb *= vignette;
      gl_FragColor = texel;
    }
  `,
};

export class PostProcessing {
  private composer: EffectComposer;
  private bloomPass: UnrealBloomPass;

  constructor(renderer: WebGLRenderer, scene: Scene, camera: Camera) {
    this.composer = new EffectComposer(renderer);

    // Render pass
    const renderPass = new RenderPass(scene, camera);
    this.composer.addPass(renderPass);

    // Bloom pass
    const resolution = new Vector2(
      renderer.domElement.clientWidth,
      renderer.domElement.clientHeight
    );
    this.bloomPass = new UnrealBloomPass(
      resolution,
      VISUAL.bloomStrength,
      VISUAL.bloomRadius,
      VISUAL.bloomThreshold
    );
    this.composer.addPass(this.bloomPass);

    // Vignette pass
    const vignettePass = new ShaderPass(VignetteShader);
    vignettePass.uniforms.darkness.value = 0.6;
    this.composer.addPass(vignettePass);
  }

  render(): void {
    this.composer.render();
  }

  setSize(width: number, height: number): void {
    this.composer.setSize(width, height);
  }

  setBloomStrength(strength: number): void {
    this.bloomPass.strength = strength;
  }
}
